import React, { FunctionComponent } from 'react';

import classnames from 'classnames';
import _ from 'lodash';
import { Button, Dropdown, DropdownButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { useMessage, MessageActionType } from 'common/message-bar/MessageBar';
import { getTextFromMetaData, getFirstLinkFromMetaData, convertCalendarDateToDateTime } from 'social/common/util/Util';
import {
    ViewState,
    PublishType,
    CreatePosts,
    ScheduledPost,
    SchedulePostResponse,
    SchedulePostsResponse,
    UpdatePost,
    PublishContent,
    ContentPostType,
    ContentPostObject,
    SourceEnum,
    SourceName,
    MediaType,
    CampaignType,
    BaseDomainMetaDatum,
    SaveContent,
    ContentOrigin,
    ContentPost,
    ResponseCode,
    FacebookTargetFields,
    EditedDraftPost,
    NewDraftPost,
    ScheduledPostState,
    Label,
    LabelSource,
    LabelMetaDatum,
    SaveLabelsResponse,
    PostOrigin,
    PublishSourceType,
    PublisherError,
    SelectedSource,
} from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import {
    postNewDraftPost,
    postEditedDraftPost,
    postPublishContent,
    postPublishPosts,
    postSaveContent,
    postUpdatePost,
    postSaveLabels,
} from 'social/publisher/PublisherFetchers';
import {
    getDatePropertiesFromScheduleDateTime,
    getSelectedGoogleFields,
    getLocationIdsFromSelectedLocations,
    getSourceKeysFromSelectedSources,
    getLocationCodesFromSelectedLocations,
    isFacebookTargetFieldsSameAsDefault,
    getNonDefaultFacebookTargetFields,
    getPostDateMetaDataFromScheduleDateTime,
    getMentionMetaDataFromContent,
    getHashtagMetaDataFromContent,
    hasHashtagInContent,
    hasMentionInContent,
    getPostListFromViewState,
} from 'social/publisher/util/Util';
import { ValidatePost } from 'social/publisher/validator/Validator';

import 'social/publisher/publish-button/PublishButton.scss';

interface Props {
    viewState: ViewState;
    onPublish?: any;
    onDone?: any;
}
type PublishButton = {
    label: string;
    handler: () => void;
};
type PublishButtons = Record<PublishType, PublishButton>;
type PublishOption = {
    label: string;
    value: PublishType;
    description: string;
};
interface SourceKeyEnumMap extends Record<SourceName, number> {
    [index: string]: number;
}
const sourceKeyEnumMap: SourceKeyEnumMap = {
    FACEBOOK: SourceEnum.FACEBOOK,
    TWITTER: SourceEnum.TWITTER,
    YOUTUBE: SourceEnum.YOUTUBE,
    INSTAGRAM: SourceEnum.INSTAGRAM,
    GOOGLE_PLACES: SourceEnum.GOOGLE_PLACES,
    LINKEDIN: SourceEnum.LINKEDIN,
    IG_STORIES: SourceEnum.INSTAGRAM,
    IG_REELS: SourceEnum.INSTAGRAM,
    TIKTOK: SourceEnum.TIKTOK,
};
const PUBLISH_OPTIONS: Array<PublishOption> = [
    {
        label: 'PUBLISH_NOW',
        value: 'PUBLISH_NOW',
        description: 'PUBLISH_IMMEDIATELY',
    },
    {
        label: 'PUBLISH_LATER',
        value: 'PUBLISH_LATER',
        description: 'SPECIFIC_DAYS_TIMES',
    },
    {
        label: 'PUBLISH_OPTIMAL_TIMES',
        value: 'OPTIMAL_TIMES',
        description: 'OPTIMAL_TIMES_DESCRIPTION',
    },
    {
        label: 'MANUAL',
        value: 'MANUAL',
        description: 'PUBLISH_MANUALLY',
    },
    {
        label: 'SAVE_AS_DRAFT',
        value: 'DRAFT',
        description: 'SAVE_FOR_LATER',
    },
];
const getFacebookTargetFieldsFromViewState = (facebookTargetFields: FacebookTargetFields): FacebookTargetFields => {
    const targetFields = getNonDefaultFacebookTargetFields(facebookTargetFields);

    if (targetFields.interests) {
        targetFields.interests = targetFields.interests.map((el) => {
            const interestWithoutValues = _.pick(el, ['key', 'name', 'audienceSize']);

            return interestWithoutValues;
        });
    }

    return targetFields;
};
const convertViewStateToCreatePostsCall = (viewState: ViewState, createdLabels: Label[]): CreatePosts => {
    const postList = getPostListFromViewState(viewState);
    const tenantId = viewState!.profile!.user!.tenantID;
    const locationIds = getLocationIdsFromSelectedLocations(
        viewState.selectedLocations,
        tenantId,
        viewState.locationSources,
    );
    const properties = [
        {
            name: 'widgetVersion',
            value: '3.1',
        },
    ];

    if (viewState.rating?.ratingID) {
        properties.push({
            name: 'ratingID',
            value: viewState.rating?.ratingID,
        });
    }

    // we allow scheduling multiple date times when creating a new post
    const postDateMetadataList = getPostDateMetaDataFromScheduleDateTime(
        viewState.scheduleDateTime,
        viewState.publishType,
    );

    const scheduledPostObj: ScheduledPost = {
        tenantID: tenantId,
        userID: viewState!.profile!.user!.id,
        agencyID: viewState!.profile!.user!.agencyID,
        postList,
        uploadType: 2,
        properties,
        multiPublish: viewState.duplicatePost || postDateMetadataList.length > 1,
        postOrigin: viewState.postOrigin || PostOrigin.DEFAULT,
        publishType: viewState.publishSourceType || PublishSourceType.API,
    };

    // optimal times
    if (viewState.sourceToPublishTime) {
        scheduledPostObj.sourceToPublishTime = viewState.sourceToPublishTime;

        if (postDateMetadataList && postDateMetadataList.length > 0) {
            _.forOwn(viewState.sourceToPublishTime, (value, key) => {
                postDateMetadataList[0].postDate = value;
                return false;
            });

            postDateMetadataList[0].scheduledDateMillis = convertCalendarDateToDateTime(
                postDateMetadataList[0].postDate,
            ).toMillis();
            postDateMetadataList[0].useLocationTimezone = true;
        }
    }

    // dm link
    if (viewState.isDMLinkAdded) {
        _.forEach(scheduledPostObj.postList, (el) => {
            const postItem = el;

            postItem.post.dmLink = viewState.isDMLinkAdded;
        });
    }

    // if scheduling a post that was a draft, it will have an id
    if (viewState.postId) {
        scheduledPostObj.id = viewState.postId as string;
    }

    if (viewState.selectedLabels && viewState.selectedLabels.length > 0) {
        scheduledPostObj.labelIds = viewState.selectedLabels.map((el) => {
            const label = el;

            if (!label.id) {
                label.id = _.result(_.find(createdLabels, { name: label.name }), 'id');
            }

            return label.id;
        });
    } else if (viewState.selectedLabelIds) {
        scheduledPostObj.labelIds = viewState.selectedLabelIds;
    }

    // target audience, only support facebook as of now
    if (
        viewState.selectedSources.FACEBOOK &&
        viewState.targetAudience &&
        viewState.targetAudience.FACEBOOK &&
        !isFacebookTargetFieldsSameAsDefault(viewState.targetAudience.FACEBOOK)
    ) {
        scheduledPostObj.targetAudienceBySource = {
            FACEBOOK: getFacebookTargetFieldsFromViewState(viewState.targetAudience.FACEBOOK),
        };
    }

    // dark post
    if (viewState.targetAudience?.FACEBOOK?.isUnpublished) {
        scheduledPostObj.dark = viewState.targetAudience.FACEBOOK.isUnpublished;
    }

    // location tagging, only support twitter as of now
    if (viewState.selectedSources.TWITTER && viewState.locationTag) {
        scheduledPostObj.locationTag = viewState.locationTag;
    }

    let isAutoApproved = false;

    if (
        (!viewState.hasSocialPermission && viewState.hasProfilePublishPermission) ||
        viewState.hasApproveAutoPermission
    ) {
        /* AutoApproved status must be set to true for GMB-based / Directory Publishing
         * if the user/tenant _does_not_ have Social because there's no path to approve a
         * post without full Social module access on the tenant level. This only applies
         * to this specific use case and only for new posts.
         */
        isAutoApproved = true;
    }

    const createPostObj: CreatePosts = {
        scheduledPost: scheduledPostObj,
        // TODO: This would be A LOT easier if it used lUids, since we already have those and wouldn't need any conversions
        locationFacets: [
            {
                name: '__location_id__',
                values: locationIds,
            },
        ],
        options: {
            partialPosting: true,
            autoApproved: isAutoApproved,
            ignoreMissingAuth: false,
            rejectLevel: 2,
        },
        tenantId,
        // update this when we support schedule multi date time at once
        postDateMetadataList,
    };

    return createPostObj;
};

const convertViewStateToUpdatePostCall = (viewState: ViewState, createdLabels: Label[]): UpdatePost => {
    const postList = getPostListFromViewState(viewState);
    const tenantId = viewState!.profile!.user!.tenantID;
    const locationIds = getLocationIdsFromSelectedLocations(
        viewState.selectedLocations,
        tenantId,
        viewState.locationSources,
    );
    // { calendarDateObject, scheduleDate, postDate } properites on schedulePost object
    let dateProperties = getDatePropertiesFromScheduleDateTime(viewState.scheduleDateTime[0], viewState.publishType);
    const scheduledPostId = viewState.postId as string;
    // TODO: camId and camType merge: do we need these for edit post?
    const scheduledPostObj: ScheduledPost = {
        tenantID: tenantId,
        userID: viewState!.profile!.user!.id,
        agencyID: viewState!.profile!.user!.agencyID,
        postList,
        uploadType: 2,
        id: scheduledPostId,
        editedFromId: scheduledPostId,
        conId: viewState.contentId || null,
        camId: viewState.campaignId || null,
        camType: viewState.campaignType || null,
        multiPublish: viewState.duplicatePost,
        postOrigin: viewState.postOrigin || PostOrigin.DEFAULT,
        publishType: viewState.publishSourceType || PublishSourceType.API,
    };

    // optimal times
    if (viewState.sourceToPublishTime) {
        scheduledPostObj.sourceToPublishTime = viewState.sourceToPublishTime;
        const scheduleDateTime: any = {};

        _.forOwn(viewState.sourceToPublishTime, (value, key) => {
            scheduleDateTime.postDate = value;
            scheduleDateTime.useLocationTimezone = true;
            return false;
        });

        dateProperties = getDatePropertiesFromScheduleDateTime(scheduleDateTime, viewState.publishType);
    }

    // dm link
    if (viewState.isDMLinkAdded) {
        _.forEach(scheduledPostObj.postList, (el) => {
            const postItem = el;

            postItem.post.dmLink = viewState.isDMLinkAdded;
        });
    }

    if (viewState.selectedLabels && viewState.selectedLabels.length > 0) {
        scheduledPostObj.labelIds = viewState.selectedLabels.map((el) => {
            const label = el;

            if (!label.id) {
                label.id = _.result(_.find(createdLabels, { name: label.name }), 'id');
            }

            return label.id;
        });
    } else if (viewState.selectedLabelIds) {
        scheduledPostObj.labelIds = viewState.selectedLabelIds;
    }

    // target audience, only support facebook as of now
    if (
        viewState.selectedSources.FACEBOOK &&
        viewState.targetAudience &&
        viewState.targetAudience.FACEBOOK &&
        !isFacebookTargetFieldsSameAsDefault(viewState.targetAudience.FACEBOOK)
    ) {
        scheduledPostObj.targetAudienceBySource = {
            FACEBOOK: getFacebookTargetFieldsFromViewState(viewState.targetAudience.FACEBOOK),
        };
    }

    // location tagging, only support twitter as of now
    if (viewState.selectedSources.TWITTER && viewState.locationTag) {
        scheduledPostObj.locationTag = viewState.locationTag;
    }

    let isAutoApproved = false;

    // Set schedule date based on publishType
    _.assign(scheduledPostObj, dateProperties);

    // TODO: This will also need to be set to true if the user is retrying a post
    if (viewState.hasApproveAutoPermission) {
        isAutoApproved = true;
    }

    const updatePostObj: UpdatePost = {
        oldScheduledPostId: scheduledPostId,
        oldPostFacets: [
            {
                name: '__socialcore_scheduled_location_post_lid__',
                values: viewState?.oldPostLocations?.map((el) => el.id) || [],
            },
        ],
        newScheduledPost: scheduledPostObj,
        newLocationFacets: [{ name: '__location_id__', values: locationIds }],
        options: {
            partialPosting: true,
            autoApproved: isAutoApproved,
            ignoreMissingAuth: false,
            rejectLevel: 2,
        },
        tenantId,
    };

    return updatePostObj;
};

const convertViewStateToNewDraftPost = (viewState: ViewState, createdLabels: Label[]): NewDraftPost => {
    const postList = getPostListFromViewState(viewState); // todo: I don't think we need a separate function for drafts but double check
    const tenantId = viewState!.profile!.user!.tenantID;
    const dateProperties = getDatePropertiesFromScheduleDateTime(viewState.scheduleDateTime[0], viewState.publishType); // might need to skip if not entered in draft
    const properties = [
        {
            name: 'widgetVersion',
            value: '3.1',
        },
    ];
    let locationUids = null;

    if (viewState.selectedLocations && viewState.selectedLocations.length > 0) {
        locationUids = viewState.selectedLocations;
    }

    if (viewState.rating?.ratingID) {
        properties.push({
            name: 'ratingID',
            value: viewState.rating?.ratingID,
        });
    }

    // we allow scheduling multiple date times when creating a new post
    const postDateMetadataList = getPostDateMetaDataFromScheduleDateTime(
        viewState.scheduleDateTime,
        viewState.publishType,
    );

    const scheduledPost: ScheduledPost = {
        scheduledPostState: ScheduledPostState.DRAFT,
        tenantID: tenantId,
        userID: viewState!.profile!.user!.id,
        agencyID: viewState!.profile!.user!.agencyID,
        postList,
        uploadType: 2,
        properties,
        multiPublish: viewState.duplicatePost || postDateMetadataList.length > 1,
        locationUids,
        postDateMetadataList,
        postOrigin: viewState.postOrigin || PostOrigin.DEFAULT,
        publishType: viewState.publishSourceType || PublishSourceType.API,
    };

    // dm link
    if (viewState.isDMLinkAdded) {
        _.forEach(scheduledPost.postList, (el) => {
            const postItem = el;

            postItem.post.dmLink = viewState.isDMLinkAdded;
        });
    }

    // Set schedule date based on publishType
    _.assign(scheduledPost, dateProperties);

    if (viewState.selectedLabels && viewState.selectedLabels.length > 0) {
        scheduledPost.labelIds = viewState.selectedLabels.map((el) => {
            const label = el;

            if (!label.id) {
                label.id = _.result(_.find(createdLabels, { name: label.name }), 'id');
            }

            return label.id;
        });
    } else if (viewState.selectedLabelIds) {
        scheduledPost.labelIds = viewState.selectedLabelIds;
    }

    // target audience, only support facebook as of now
    if (
        viewState.selectedSources.FACEBOOK &&
        viewState.targetAudience &&
        viewState.targetAudience.FACEBOOK &&
        !isFacebookTargetFieldsSameAsDefault(viewState.targetAudience.FACEBOOK)
    ) {
        scheduledPost.targetAudienceBySource = {
            FACEBOOK: getFacebookTargetFieldsFromViewState(viewState.targetAudience.FACEBOOK),
        };
    }

    // let isAutoApproved = false;

    // if (
    //     (!viewState.hasSocialPermission && viewState.hasProfilePublishPermission) ||
    //     viewState.hasApproveAutoPermission
    // ) {
    //     /* AutoApproved status must be set to true for GMB-based / Directory Publishing
    //      * if the user/tenant _does_not_ have Social because there's no path to approve a
    //      * post without full Social module access on the tenant level. This only applies
    //      * to this specific use case and only for new posts.
    //      */
    //     isAutoApproved = true;
    // }

    const newDraftPost: NewDraftPost = {
        scheduledPost,
    };

    return newDraftPost;
};

const convertViewStateToEditedDraftPost = (viewState: ViewState, createdLabels: Label[]): EditedDraftPost => {
    const postList = getPostListFromViewState(viewState);
    const tenantId = viewState!.profile!.user!.tenantID;

    const dateProperties = getDatePropertiesFromScheduleDateTime(viewState.scheduleDateTime[0], viewState.publishType); // might need to skip if not entered in draft
    const postId = viewState.postId as string;

    let locationUids = null;

    if (viewState.selectedLocations && viewState.selectedLocations.length > 0) {
        locationUids = viewState.selectedLocations;
    }

    // we allow scheduling multiple date times when creating a new post
    const postDateMetadataList = getPostDateMetaDataFromScheduleDateTime(
        viewState.scheduleDateTime,
        viewState.publishType,
    );

    const scheduledPost: ScheduledPost = {
        scheduledPostState: ScheduledPostState.DRAFT,
        tenantID: tenantId,
        userID: viewState!.profile!.user!.id,
        agencyID: viewState!.profile!.user!.agencyID,
        postList,
        uploadType: 2,
        id: postId,
        multiPublish: viewState.duplicatePost,
        locationUids,
        postDateMetadataList,
        postOrigin: viewState.postOrigin || PostOrigin.DEFAULT,
        publishType: viewState.publishSourceType || PublishSourceType.API,
    };

    // dm link
    if (viewState.isDMLinkAdded) {
        _.forEach(scheduledPost.postList, (el) => {
            const postItem = el;

            postItem.post.dmLink = viewState.isDMLinkAdded;
        });
    }

    // labels
    if (viewState.selectedLabels && viewState.selectedLabels.length > 0) {
        scheduledPost.labelIds = viewState.selectedLabels.map((el) => {
            const label = el;

            if (!label.id) {
                label.id = _.result(_.find(createdLabels, { name: label.name }), 'id');
            }

            return label.id;
        });
    } else if (viewState.selectedLabelIds) {
        scheduledPost.labelIds = viewState.selectedLabelIds;
    }

    // target audience, only support facebook as of now
    if (
        viewState.selectedSources.FACEBOOK &&
        viewState.targetAudience &&
        viewState.targetAudience.FACEBOOK &&
        !isFacebookTargetFieldsSameAsDefault(viewState.targetAudience.FACEBOOK)
    ) {
        scheduledPost.targetAudienceBySource = {
            FACEBOOK: getFacebookTargetFieldsFromViewState(viewState.targetAudience.FACEBOOK),
        };
    }
    // let isAutoApproved = false;

    // Set schedule date based on publishType
    _.assign(scheduledPost, dateProperties);

    // // TODO: This will also need to be set to true if the user is retrying a post
    // if (viewState.hasApproveAutoPermission) {
    //     isAutoApproved = true;
    // }

    const editedDraftPost: EditedDraftPost = {
        scheduledPostId: postId,
        scheduledPost,
    };

    return editedDraftPost;
};

const getContentObjectFromViewState = (viewState: ViewState): ContentPost => {
    const sources = getSourceKeysFromSelectedSources(viewState.selectedSources).map((el) => sourceKeyEnumMap[el]);
    const contentPostObject: ContentPostObject = {
        post: {},
        sources,
    };
    let contentPostType: ContentPostType = ContentPostType.TEXT;

    if (viewState.postType === 'LINK') {
        contentPostType = ContentPostType.LINK;
        if (viewState.urlMetaData) {
            const linkUrl = getFirstLinkFromMetaData(viewState.content) as string;

            contentPostObject.post.link = {
                message: getTextFromMetaData(viewState.content),
                linkUrl,
            };
            // Mentions
            if (hasMentionInContent(viewState.content)) {
                contentPostObject.post.link.mentionMetaData = getMentionMetaDataFromContent(viewState.content);
            }
            // Hashtags
            if (hasHashtagInContent(viewState.content)) {
                contentPostObject.post.link.hashtagMetaData = getHashtagMetaDataFromContent(viewState.content);
            }
        }
    }
    if (viewState.postType === 'MEDIA') {
        if (viewState.media && viewState.media.length > 0) {
            contentPostType = ContentPostType.MEDIA;
            let mediaType = MediaType.SINGLE_IMAGE;

            if (viewState.media.length === 1) {
                if (viewState.media[0].isVideo) {
                    mediaType = MediaType.SINGLE_VIDEO;
                }
            } else {
                mediaType = MediaType.MULTI_IMAGE;
            }
            contentPostObject.post.media = {
                standardMediaPost: {
                    mediaType,
                    mediaURLs: viewState.media.map((mediaObj) => {
                        return mediaObj.url;
                    }),
                    message: getTextFromMetaData(viewState.content),
                },
            };
            // Mentions
            if (hasMentionInContent(viewState.content)) {
                contentPostObject.post.media.standardMediaPost.mentionMetaData = getMentionMetaDataFromContent(
                    viewState.content,
                );
            }
            // Hashtags
            if (hasHashtagInContent(viewState.content)) {
                contentPostObject.post.media.standardMediaPost.hashtagMetaData = getHashtagMetaDataFromContent(
                    viewState.content,
                );
            }
        } else {
            contentPostType = ContentPostType.TEXT;
            contentPostObject.post.text = { message: getTextFromMetaData(viewState.content) };

            // Mentions
            if (hasMentionInContent(viewState.content)) {
                contentPostObject.post.text.mentionMetaData = getMentionMetaDataFromContent(viewState.content);
            }
            // Hashtags
            if (hasHashtagInContent(viewState.content)) {
                contentPostObject.post.text.hashtagMetaData = getHashtagMetaDataFromContent(viewState.content);
            }
        }
    }

    const contentObject: ContentPost = {
        postType: contentPostType,
        posts: [contentPostObject],
    };

    // GOOGLE_PLACES values
    if (viewState.selectedSources.GOOGLE_PLACES) {
        const googleFields = getSelectedGoogleFields(viewState.googleFields);

        _.assign(contentObject, googleFields);
    }

    // YOUTUBE values
    if (viewState.selectedSources.YOUTUBE) {
        const youtubeFields = { ...viewState.youtubeFields };

        _.assign(contentObject, { title: viewState.title }, youtubeFields);
    }

    return contentObject;
};

const convertViewStateToPublishContentCall = (viewState: ViewState, createdLabels: Label[]): PublishContent => {
    const tenantId = viewState!.profile!.user!.tenantID;
    const contentId = viewState.contentId as string;
    const campaignId = viewState.campaignId as string;
    const baseDomainMetaDatum = viewState.contentData?.baseDomainMetaDatum as BaseDomainMetaDatum;
    const postOrigin = viewState.postOrigin || PostOrigin.DEFAULT;
    const ratingID = viewState.rating?.ratingID;
    const locationIds = getLocationIdsFromSelectedLocations(
        viewState.selectedLocations,
        tenantId,
        viewState.locationSources,
    );
    // { calendarDateObject, scheduleDate, postDate } properites on schedulePost object
    const dateProperties = getDatePropertiesFromScheduleDateTime(viewState.scheduleDateTime[0], viewState.publishType);
    const contentObject = getContentObjectFromViewState(viewState);
    // publish regular content
    let campaignType = 'SHADOW';

    // publish campaign content
    if (viewState.campaignType === CampaignType.PUSH) {
        campaignType = 'PUSH';
    }

    const mediaAltText: string[][] = [];

    if (viewState.mediaPathsToAltText) {
        Object.keys(viewState.mediaPathsToAltText).forEach((key) => {
            // backend expects string[][] because mongo doesn't allow '.' in map keys
            mediaAltText.push([key, viewState.mediaPathsToAltText![key]]);
        });
    }

    const publishContent: PublishContent = {
        scheduledContent: {
            contentMetaDatum: {
                id: contentId,
                campaignId,
                content: contentObject,
                baseDomainMetaDatum,
                duplicatedFromId: viewState.contentData?.duplicatedFromId,
                postOrigin,
                ratingID,
                mediaPathsToAltText: mediaAltText,
            },
            scheduleDate: dateProperties.postDate,
            calendarDateObject: dateProperties.calendarDateObject,
            locationIds,
            properties: [
                { name: 'campaignId', value: campaignId },
                { name: 'contentId', value: contentId },
                { name: 'campaignType', value: campaignType },
                { name: 'savedFrom', value: 'PUBLISHER_V2' },
            ],
        },
        tenantId,
    };

    if (viewState.selectedLabels && viewState.selectedLabels.length > 0) {
        publishContent.scheduledContent.contentMetaDatum.labelIds = viewState.selectedLabels.map((el) => {
            const label = el;

            if (!label.id) {
                label.id = _.result(_.find(createdLabels, { name: label.name }), 'id');
            }

            return label.id;
        });
    } else if (viewState.selectedLabelIds) {
        publishContent.scheduledContent.contentMetaDatum.labelIds = viewState.selectedLabelIds;
    }

    return publishContent;
};

const convertViewStateToSaveCampaignPostCall = (viewState: ViewState): SaveContent => {
    const tenantId = viewState!.profile!.user!.tenantID;
    const contentObject = getContentObjectFromViewState(viewState);
    const postOrigin = viewState.postOrigin || PostOrigin.DEFAULT;
    const mediaAltText: string[][] = [];

    if (viewState.mediaPathsToAltText) {
        Object.keys(viewState.mediaPathsToAltText).forEach((key) => {
            // backend expects string[][] because mongo doesn't allow '.' in map keys
            mediaAltText.push([key, viewState.mediaPathsToAltText![key]]);
        });
    }

    const saveContent: SaveContent = {
        content: {
            contentMetaDatum: {
                content: contentObject,
                origin: ContentOrigin.CAMPAIGN,
                campaignId: viewState.campaignId as string,
                baseDomainMetaDatum: { locationId: 'g' },
                postOrigin,
                mediaPathsToAltText: mediaAltText,
            },
            facets: { g: [] },
        },
        tenantId,
    };

    if (viewState.publishType === 'PUBLISH_LATER') {
        const dateProperties = getDatePropertiesFromScheduleDateTime(
            viewState.scheduleDateTime[0],
            viewState.publishType,
        );

        saveContent.content.contentMetaDatum.scheduleDateObject = dateProperties.postDate;
        saveContent.content.contentMetaDatum.calendarDateObject = dateProperties.calendarDateObject;
    }

    // edit campaign post
    if (viewState.contentId) {
        saveContent.content.contentMetaDatum.id = viewState.contentId;
        saveContent.content.contentMetaDatum.baseDomainMetaDatum = viewState.contentData
            ?.baseDomainMetaDatum as BaseDomainMetaDatum;
    }

    return saveContent;
};

const convertViewStateToSaveContentCall = (viewState: ViewState, createdLabels: Label[]): SaveContent => {
    const tenantId = viewState!.profile!.user!.tenantID;
    const contentObject = getContentObjectFromViewState(viewState);
    const postOrigin = viewState.postOrigin || PostOrigin.DEFAULT;
    const ratingID = viewState.rating?.ratingID;
    const mediaAltText: string[][] = [];

    if (viewState.mediaPathsToAltText) {
        Object.keys(viewState.mediaPathsToAltText).forEach((key) => {
            // backend expects string[][] because mongo doesn't allow '.' in map keys
            mediaAltText.push([key, viewState.mediaPathsToAltText![key]]);
        });
    }

    const saveContent: SaveContent = {
        content: {
            contentMetaDatum: {
                content: contentObject,
                origin: ContentOrigin.CUSTOM,
                baseDomainMetaDatum: { locationId: 'g' },
                postOrigin,
                ratingID,
                mediaPathsToAltText: mediaAltText,
            },
            facets: { g: [] },
        },
        tenantId,
    };

    if (viewState.selectedLabels && viewState.selectedLabels.length > 0) {
        saveContent.content.contentMetaDatum.labelIds = viewState.selectedLabels.map((el) => {
            const label = el;

            if (!label.id) {
                label.id = _.result(_.find(createdLabels, { name: label.name }), 'id');
            }

            return label.id;
        });
    } else if (viewState.selectedLabelIds) {
        saveContent.content.contentMetaDatum.labelIds = viewState.selectedLabelIds;
    }

    if (viewState.selectedLocationGroup === 'SELECT_LOCATIONS') {
        const locationCodes = getLocationCodesFromSelectedLocations(
            viewState.selectedLocations,
            viewState.locationSources,
        );

        saveContent.content.facets = { location: locationCodes };
    } else if (
        viewState.selectedLocationGroup === 'MY_LOCATIONS' ||
        viewState.selectedLocationGroup === 'MY_LOCATION'
    ) {
        let myLocationCodes: string[] = [];

        if (viewState.myLocations) {
            myLocationCodes = viewState.myLocations.map((el) => el.code);
        }

        saveContent.content.facets = { location: myLocationCodes };
    }
    if (viewState.title) {
        saveContent.content.contentMetaDatum.title = viewState.title;
    }

    if (typeof viewState.isSmartReady === 'boolean') {
        saveContent.content.contentMetaDatum.smartReady = viewState.isSmartReady;
    }

    // edit existing content
    if (viewState.contentId) {
        saveContent.content.contentMetaDatum.id = viewState.contentId;
        saveContent.content.contentMetaDatum.baseDomainMetaDatum = viewState.contentData
            ?.baseDomainMetaDatum as BaseDomainMetaDatum;
        saveContent.content.contentMetaDatum.campaignId = viewState.campaignId as string;
    }

    return saveContent;
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PublishButton: FunctionComponent<Props> = ({ viewState, onPublish, onDone }) => {
    const { t } = useTranslation(['social/publisher']);
    const dispatch = usePublisherDispatch();
    const messageDispatch = useMessage()[1];
    const showPostSummary = (responseData: SchedulePostResponse | SchedulePostsResponse): void => {
        dispatch({ type: 'set-schedule-post-response', payload: { value: responseData } });
        dispatch({ type: 'set-is-show-post-summary', payload: { value: true } });
    };

    const savePost = async (): Promise<void> => {
        let postResponse;

        dispatch({ type: 'add-promises-in-transit', payload: { value: 'PUBLISH' } });

        const labelsToCreate = viewState.selectedLabels.filter((el) => !el.id);
        let createdLabels: Label[] = [];

        if (labelsToCreate.length > 0) {
            const labelMetaDatums: LabelMetaDatum[] = [];

            _.forEach(labelsToCreate, (labelToCreate) => {
                const labelMetaDatum: LabelMetaDatum = {
                    label: labelToCreate,
                    labelSource: LabelSource.User,
                };

                labelMetaDatums.push(labelMetaDatum);
            });

            const saveLabelsResponse: SaveLabelsResponse = await postSaveLabels(
                viewState!.profile!.user!.tenantID,
                labelMetaDatums,
            );

            createdLabels = saveLabelsResponse.labels;
        }

        try {
            if (viewState.editorMode === 'POST_PUBLISH') {
                if (viewState.postId && viewState.scheduledPostState !== ScheduledPostState.DRAFT) {
                    postResponse = await trackPromise(
                        postUpdatePost(
                            viewState!.profile!.user!.tenantID,
                            convertViewStateToUpdatePostCall(viewState, createdLabels),
                        ),
                        'social-publisher-editor',
                    );
                } else {
                    postResponse = await trackPromise(
                        postPublishPosts(
                            viewState!.profile!.user!.tenantID,
                            convertViewStateToCreatePostsCall(viewState, createdLabels),
                        ),
                        'social-publisher-editor',
                    );
                }
            } else if (viewState.editorMode === 'CONTENT_PUBLISH' || viewState.editorMode === 'CAMPAIGN_PUBLISH') {
                postResponse = await trackPromise(
                    postPublishContent(
                        viewState!.profile!.user!.tenantID,
                        convertViewStateToPublishContentCall(viewState, createdLabels),
                    ),
                    'social-publisher-editor',
                );
            }
        } catch (err) {
            const errMsg = _.get(err, 'response.data.error.detail[0].message');

            messageDispatch({
                type: MessageActionType.ShowError,
                text: errMsg || 'Unknown posting error. Please try again.',
            });
        } finally {
            dispatch({ type: 'remove-promises-in-transit', payload: { value: 'PUBLISH' } });
        }

        if (postResponse) {
            showPostSummary(postResponse);
            if (onPublish && typeof onPublish === 'function') {
                onPublish();
            }
        }
    };

    const saveCampaignPost = async (): Promise<void> => {
        let postResponse;

        dispatch({ type: 'add-promises-in-transit', payload: { value: 'PUBLISH' } });

        try {
            postResponse = await trackPromise(
                postSaveContent(viewState!.profile!.user!.tenantID, convertViewStateToSaveCampaignPostCall(viewState)),
                'social-publisher-editor',
            );
        } catch (err) {
            const errMsg = _.get(err, 'response.data.error.detail.error[0].message');

            messageDispatch({
                type: MessageActionType.ShowError,
                text: errMsg || 'Unknown posting error. Please try again.',
            });
        } finally {
            dispatch({ type: 'remove-promises-in-transit', payload: { value: 'PUBLISH' } });
        }

        if (postResponse?.response.responseCode === ResponseCode.Success) {
            const text = viewState.contentId ? 'Post updated successfully.' : 'Post added successfully.';

            messageDispatch({
                type: MessageActionType.ShowSuccess,
                text,
            });
            if (onPublish && typeof onPublish === 'function') {
                onPublish();
            }
            if (onDone && typeof onDone === 'function') {
                onDone();
            }
        }
    };

    const saveLibraryContent = async (): Promise<void> => {
        let postResponse;

        dispatch({ type: 'add-promises-in-transit', payload: { value: 'PUBLISH' } });

        const labelsToCreate = viewState.selectedLabels.filter((el) => !el.id);
        let createdLabels: Label[] = [];

        if (labelsToCreate.length > 0) {
            const labelMetaDatums: LabelMetaDatum[] = [];

            _.forEach(labelsToCreate, (labelToCreate) => {
                const labelMetaDatum: LabelMetaDatum = {
                    label: labelToCreate,
                    labelSource: LabelSource.User,
                };

                labelMetaDatums.push(labelMetaDatum);
            });

            const saveLabelsResponse: SaveLabelsResponse = await postSaveLabels(
                viewState!.profile!.user!.tenantID,
                labelMetaDatums,
            );

            createdLabels = saveLabelsResponse.labels;
        }

        try {
            postResponse = await trackPromise(
                postSaveContent(
                    viewState!.profile!.user!.tenantID,
                    convertViewStateToSaveContentCall(viewState, createdLabels),
                ),
                'social-publisher-editor',
            );
        } catch (err) {
            const errMsg = _.get(err, 'response.data.error.detail.error[0].message');

            messageDispatch({
                type: MessageActionType.ShowError,
                text: errMsg || 'Unknown posting error. Please try again.',
            });
        } finally {
            dispatch({ type: 'remove-promises-in-transit', payload: { value: 'PUBLISH' } });
        }

        if (postResponse?.response.responseCode === ResponseCode.Success) {
            const text = viewState.contentId ? 'Content updated successfully.' : 'Content created successfully.';

            messageDispatch({
                type: MessageActionType.ShowSuccess,
                text,
            });
            if (onPublish && typeof onPublish === 'function') {
                onPublish();
            }
            if (onDone && typeof onDone === 'function') {
                onDone();
            }
        }
    };

    const saveDraftPost = async (): Promise<void> => {
        let postResponse;

        dispatch({ type: 'add-promises-in-transit', payload: { value: 'PUBLISH' } });

        const labelsToCreate = viewState.selectedLabels.filter((el) => !el.id);
        let createdLabels: Label[] = [];

        if (labelsToCreate.length > 0) {
            const labelMetaDatums: LabelMetaDatum[] = [];

            _.forEach(labelsToCreate, (labelToCreate) => {
                const labelMetaDatum: LabelMetaDatum = {
                    label: labelToCreate,
                    labelSource: LabelSource.User,
                };

                labelMetaDatums.push(labelMetaDatum);
            });

            const saveLabelsResponse: SaveLabelsResponse = await postSaveLabels(
                viewState!.profile!.user!.tenantID,
                labelMetaDatums,
            );

            createdLabels = saveLabelsResponse.labels;
        }

        try {
            if (viewState.postId) {
                postResponse = await trackPromise(
                    postEditedDraftPost(
                        viewState!.profile!.user!.tenantID,
                        convertViewStateToEditedDraftPost(viewState, createdLabels),
                    ),
                    'social-publisher-editor',
                );
            } else {
                postResponse = await trackPromise(
                    postNewDraftPost(
                        viewState!.profile!.user!.tenantID,
                        convertViewStateToNewDraftPost(viewState, createdLabels),
                    ),
                    'social-publisher-editor',
                );
            }
        } catch (err) {
            const errMsg = _.get(err, 'response.data.error.detail[0].message');

            messageDispatch({
                type: MessageActionType.ShowError,
                text: errMsg || 'Unknown posting error. Please try again.',
            });
        } finally {
            dispatch({ type: 'remove-promises-in-transit', payload: { value: 'PUBLISH' } });
        }

        if (postResponse) {
            if (onDone && typeof onDone === 'function') {
                onDone();
            }
        }
    };

    const handlePublishPost = (): void => {
        let errors: PublisherError[] = [];

        if (!viewState.isCustomizingBySource) {
            errors = ValidatePost(viewState);
        } else {
            Object.keys(viewState.selectedSources).forEach((sourceName) => {
                const sourceState = viewState.sourceStates[sourceName] || viewState.sourceStates.ALL_SOURCES;
                // We only want to validate post for a single source
                const newSelectedSources: SelectedSource = {
                    FACEBOOK: false,
                    TWITTER: false,
                    INSTAGRAM: false,
                    GOOGLE_PLACES: false,
                    LINKEDIN: false,
                    YOUTUBE: false,
                    IG_STORIES: false,
                    IG_REELS: false,
                    TIKTOK: false,
                };

                newSelectedSources[sourceName] = true;

                if (
                    sourceState &&
                    viewState.selectedSources[sourceName] &&
                    sourceName !== viewState.customizeSourceKey
                ) {
                    const updatedState = { ...viewState, ...sourceState };

                    updatedState.selectedSources = newSelectedSources;
                    errors = errors.concat(ValidatePost(updatedState));
                }
            });

            const newSelectedSources: SelectedSource = {
                FACEBOOK: false,
                TWITTER: false,
                INSTAGRAM: false,
                GOOGLE_PLACES: false,
                LINKEDIN: false,
                YOUTUBE: false,
                IG_STORIES: false,
                IG_REELS: false,
                TIKTOK: false,
            };

            newSelectedSources[viewState.customizeSourceKey] = true;

            const currentViewState = { ...viewState };

            currentViewState.selectedSources = newSelectedSources;
            errors = errors.concat(ValidatePost(currentViewState));
        }

        if (viewState.promisesInTransit.length > 0) {
            return;
        }

        if (errors.length > 0) {
            dispatch({
                type: 'set-publisher-errors',
                payload: {
                    value: errors,
                },
            });
            return;
        }

        if (viewState.editorMode === 'CAMPAIGN_CREATE') {
            saveCampaignPost();
        } else {
            savePost();
        }
    };
    const handlePublishManual = (): void => {
        let errors: PublisherError[] = [];

        if (!viewState.isCustomizingBySource) {
            errors = ValidatePost(viewState);
        } else {
            Object.keys(viewState.selectedSources).forEach((sourceName) => {
                const sourceState = viewState.sourceStates[sourceName] || viewState.sourceStates.ALL_SOURCES;
                // We only want to validate post for a single source
                const newSelectedSources: SelectedSource = {
                    FACEBOOK: false,
                    TWITTER: false,
                    INSTAGRAM: false,
                    GOOGLE_PLACES: false,
                    LINKEDIN: false,
                    YOUTUBE: false,
                    IG_STORIES: false,
                    IG_REELS: false,
                    TIKTOK: false,
                };

                newSelectedSources[sourceName] = true;

                if (
                    sourceState &&
                    viewState.selectedSources[sourceName] &&
                    sourceName !== viewState.customizeSourceKey
                ) {
                    const updatedState = { ...viewState, ...sourceState };

                    errors = errors.concat(ValidatePost(updatedState));
                }
            });

            const newSelectedSources: SelectedSource = {
                FACEBOOK: false,
                TWITTER: false,
                INSTAGRAM: false,
                GOOGLE_PLACES: false,
                LINKEDIN: false,
                YOUTUBE: false,
                IG_STORIES: false,
                IG_REELS: false,
                TIKTOK: false,
            };

            newSelectedSources[viewState.customizeSourceKey] = true;

            const currentViewState = { ...viewState };

            currentViewState.selectedSources = newSelectedSources;
            errors = errors.concat(ValidatePost(currentViewState));
        }

        if (viewState.promisesInTransit.length > 0) {
            return;
        }

        if (errors.length > 0) {
            dispatch({
                type: 'set-publisher-errors',
                payload: {
                    value: errors,
                },
            });
            return;
        }

        saveCampaignPost();
    };
    const handleSubmitContent = (): void => {
        const errors = ValidatePost(viewState);

        if (viewState.promisesInTransit.length > 0) {
            return;
        }

        if (errors.length > 0) {
            dispatch({
                type: 'set-publisher-errors',
                payload: {
                    value: errors,
                },
            });
            return;
        }

        saveLibraryContent();
    };
    const handleSaveDraftPost = (): void => {
        let errors: PublisherError[] = [];

        if (!viewState.isCustomizingBySource) {
            errors = ValidatePost(viewState);
        } else {
            Object.keys(viewState.selectedSources).forEach((sourceName) => {
                const sourceState = viewState.sourceStates[sourceName] || viewState.sourceStates.ALL_SOURCES;
                // We only want to validate post for a single source
                const newSelectedSources: SelectedSource = {
                    FACEBOOK: false,
                    TWITTER: false,
                    INSTAGRAM: false,
                    GOOGLE_PLACES: false,
                    LINKEDIN: false,
                    YOUTUBE: false,
                    IG_STORIES: false,
                    IG_REELS: false,
                    TIKTOK: false,
                };

                newSelectedSources[sourceName] = true;

                if (
                    sourceState &&
                    viewState.selectedSources[sourceName] &&
                    sourceName !== viewState.customizeSourceKey
                ) {
                    const updatedState = { ...viewState, ...sourceState };

                    errors = errors.concat(ValidatePost(updatedState));
                }
            });

            const newSelectedSources: SelectedSource = {
                FACEBOOK: false,
                TWITTER: false,
                INSTAGRAM: false,
                GOOGLE_PLACES: false,
                LINKEDIN: false,
                YOUTUBE: false,
                IG_STORIES: false,
                IG_REELS: false,
                TIKTOK: false,
            };

            newSelectedSources[viewState.customizeSourceKey] = true;

            const currentViewState = { ...viewState };

            currentViewState.selectedSources = newSelectedSources;
            errors = errors.concat(ValidatePost(currentViewState));
        }

        if (viewState.promisesInTransit.length > 0) {
            return;
        }

        if (errors.length > 0) {
            dispatch({
                type: 'set-publisher-errors',
                payload: {
                    value: errors,
                },
            });
            return;
        }

        saveDraftPost();
    };

    const currentPublishType: PublishType = viewState.publishType;
    const PUBLISH_BUTTONS: PublishButtons = {
        PUBLISH_NOW: {
            label: 'PUBLISH_NOW',
            handler: handlePublishPost,
        },
        PUBLISH_LATER: {
            label: 'PUBLISH_LATER',
            handler: handlePublishPost,
        },
        OPTIMAL_TIMES: {
            label: 'PUBLISH_OPTIMAL_TIMES',
            handler: handlePublishPost,
        },
        MANUAL: {
            label: 'MANUAL',
            handler: handlePublishManual,
        },
        DRAFT: {
            label: 'SAVE_AS_DRAFT',
            handler: handleSaveDraftPost,
        },
    };
    const publishOptions = PUBLISH_OPTIONS.filter((el) => viewState.enabledPublishTypes.indexOf(el.value) > -1);
    const publishButtonClass = classnames('publish-button', {
        disabled: viewState.promisesInTransit.length > 0,
    });
    const publishDropdownClass = classnames('publish-options', {
        disabled: viewState.promisesInTransit.length > 0,
    });

    return (
        <div className="r4e-social-publish-button">
            {viewState.editorMode === 'CONTENT_CREATE' ? (
                <Button className={publishButtonClass} onClick={handleSubmitContent} variant="primary">
                    <span>{t('CREATE')}</span>
                </Button>
            ) : (
                <>
                    <Button
                        className={publishButtonClass}
                        onClick={PUBLISH_BUTTONS[currentPublishType].handler}
                        variant="primary"
                    >
                        <span>{t(PUBLISH_BUTTONS[currentPublishType].label)}</span>
                    </Button>
                    <DropdownButton
                        id="publish-options-dropdown"
                        title=""
                        className={publishDropdownClass}
                        disabled={viewState.promisesInTransit.length > 0 || viewState.isPublishOptionsLocked}
                    >
                        {publishOptions.map((option) => {
                            return (
                                <Dropdown.Item
                                    key={option.value}
                                    onClick={(): void =>
                                        dispatch({ type: 'set-publish-type', payload: { value: option.value } })
                                    }
                                    className="publish-option"
                                >
                                    <div className="publish-options-checkmark">
                                        {currentPublishType === option.value && <i className="icon icon-check" />}
                                    </div>
                                    <div className="publish-options-label">{t(option.label)}</div>
                                    <div className="publish-options-description">{t(option.description)}</div>
                                </Dropdown.Item>
                            );
                        })}
                    </DropdownButton>
                </>
            )}
        </div>
    );
};
